/**
 * Name: Tablist
 * Description: Tablist pattern
 */

import Component from "@lunaweb/vega-reactor-js/src/patterns/Component";

export default class Tablist extends Component {

  static settings = {};

  #tabs;
  #current;

  #handleTabClick;
  #handleTabKeyup;
  #handleTabKeydown;

  constructor (element, options = {}) {
    super(element, {
      ...Tablist.settings,
      ...options
    });

    this.#tabs = element.querySelectorAll('[data-tablist-target]');
    this.#current = null;

    this.#handleTabClick = this._handleTabClick.bind(this);
    this.#handleTabKeyup = this._handleTabKeyup.bind(this);
    this.#handleTabKeydown = this._handleTabKeydown.bind(this);
  }

  deactivate (tab) {
    const id = tab.getAttribute('aria-controls');
    const tabpanel = document.getElementById(id);
    tab.setAttribute('aria-selected', 'false');
    tab.setAttribute('tabindex', '-1');
    tabpanel.setAttribute('hidden', '');
    this.#current = null;
  }

  activate (tab) {
    const id = tab.getAttribute('aria-controls');
    const tabpanel = document.getElementById(id);
    tab.setAttribute('aria-selected', 'true');
    tab.removeAttribute('tabindex');
    tabpanel.removeAttribute('hidden');
    this.#current = tab;
  }

  _handleTabClick (e) {
    e.preventDefault();
    this.deactivate(this.#current);
    this.activate(e.currentTarget);
  }

  _handleTabKeyup (e) {
    let index = null;

    if (e.key === 'ArrowRight' || e.key === 'Right') {
      e.preventDefault();
      index = (e.currentTarget === this.#tabs[this.#tabs.length - 1]) ? 0 : Array.prototype.indexOf.call(this.#tabs, e.currentTarget) + 1;
    } else if (e.key === 'ArrowLeft' || e.key === 'Left') {
      e.preventDefault();
      index = (e.currentTarget === this.#tabs[0]) ? this.#tabs.length - 1 : Array.prototype.indexOf.call(this.#tabs, e.currentTarget) - 1;
    }

    if (index !== null) {
      this.deactivate(e.currentTarget);
      this.activate(this.#tabs[index]);
      this.#current.focus();
    }
  }

  _handleTabKeydown (e) {
    let index = null;

    if (e.key === 'Home') {
      index = 0;
    } else if (e.key === 'End') {
      index = this.#tabs.length - 1;
    }

    if (index !== null) {
      e.preventDefault();
      this.deactivate(e.currentTarget);
      this.activate(this.#tabs[index]);
      this.#current.focus();
    }
  }

  mount () {
    this.#current = this.#tabs['0'];
    this.element.setAttribute('role', 'tablist');

    this.#tabs.forEach((tab) => {
      const id = tab.getAttribute('data-tablist-target');
      const tabpanel = document.getElementById(id);
      const title = tabpanel.querySelector('[data-tablist-title]');

      if (tab.getAttribute('aria-selected') === 'true') {
        this.#current = tab;
      }

      tab.setAttribute('id', `tab-${id}`);
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
      tab.setAttribute('aria-controls', id);
      tabpanel.setAttribute('role', 'tabpanel');
      tabpanel.setAttribute('aria-labelledby', `tab-${id}`);
      tabpanel.setAttribute('hidden', '');

      if (title) {
        title.setAttribute('hidden', '');
      }

      tab.addEventListener('click', this.#handleTabClick);
      tab.addEventListener('keyup', this.#handleTabKeyup);
      tab.addEventListener('keydown', this.#handleTabKeydown);
    });

    this.activate(this.#current);

    super.mount();
  }

  unmount () {
    this.element.removeAttribute('role');

    this.#tabs.forEach((tab) => {
      const id = tab.getAttribute('data-tablist-target');
      const tabpanel = document.getElementById(id);
      const title = tabpanel.querySelector('[data-tablist-title]');

      tab.removeAttribute('id');
      tab.removeAttribute('role');
      tab.removeAttribute('aria-selected');
      tab.removeAttribute('tabindex');
      tab.removeAttribute('aria-controls');
      tabpanel.removeAttribute('role');
      tabpanel.removeAttribute('aria-labelledby');
      tabpanel.removeAttribute('hidden');

      if (title) {
        title.removeAttribute('hidden');
      }

      tab.removeEventListener('click', this.#handleTabClick);
      tab.removeEventListener('keyup', this.#handleTabKeyup);
      tab.removeEventListener('keydown', this.#handleTabKeydown);
    });

    this.#current = null;

    super.unmount();
  }
}
